import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage, BACKEND_URL } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { formatCLP, formatUSD, computeSale } from "../lib/format";
import { trackEvent } from "../lib/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export default function CheckoutPanel({ product, bundle }) {
    const target = product || bundle;
    const kind = bundle ? "bundle" : "product";
    const detailUrl = bundle ? `/bundles/${bundle.slug}` : `/plugins/${product.slug}`;
    const { user } = useAuth();
    const { cart, addItem } = useCart();
    const navigate = useNavigate();
    const [providers, setProviders] = useState(null);
    const [loading, setLoading] = useState("");
    const [trialEligibility, setTrialEligibility] = useState(null);

    // ---- Coupon state ----
    const [couponInput, setCouponInput] = useState("");
    const [couponApplied, setCouponApplied] = useState(null); // {code, discount_pct, expires_at}
    const [couponError, setCouponError] = useState("");
    const [couponValidating, setCouponValidating] = useState(false);

    useEffect(() => {
        api.get("/checkout/providers").then(({ data }) => setProviders(data)).catch(() => setProviders({}));
    }, []);

    // Trial eligibility — only for products + logged-in users
    useEffect(() => {
        if (!product || !user) { setTrialEligibility(null); return; }
        api.get(`/checkout/trial/eligibility?product_id=${product.id}`)
            .then(({ data }) => setTrialEligibility(data))
            .catch(() => setTrialEligibility(null));
    }, [product, user]);

    // Auto-fill coupon from URL ?coupon=CODE
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("coupon");
        if (code && user) {
            setCouponInput(code.toUpperCase());
            // Auto-apply after a short delay
            const t = setTimeout(() => applyCoupon(code), 300);
            return () => clearTimeout(t);
        }
    }, [user, target?.id]);

    const applyCoupon = async (overrideCode) => {
        const code = (overrideCode || couponInput).trim().toUpperCase();
        if (!code) return;
        if (!user) { toast.info("Log in to apply a coupon"); return; }
        setCouponValidating(true); setCouponError("");
        try {
            const { data } = await api.post("/coupons/validate", {
                code,
                product_id: kind === "product" ? product.id : null,
                bundle_id: kind === "bundle" ? bundle.id : null,
            });
            if (data.valid) {
                setCouponApplied({ code: data.code, discount_pct: data.discount_pct, expires_at: data.expires_at });
                setCouponError("");
                toast.success(`Coupon ${data.code}: ${data.discount_pct}% off applied`);
            } else {
                setCouponApplied(null);
                setCouponError(data.message || "Invalid coupon");
            }
        } catch (err) {
            setCouponApplied(null);
            setCouponError(apiErrorMessage(err));
        } finally {
            setCouponValidating(false);
        }
    };

    const removeCoupon = () => {
        setCouponApplied(null);
        setCouponInput("");
        setCouponError("");
    };

    // Sale on the product — sale price is the "effective base" and coupons stack on top
    const sale = kind === "product" ? computeSale(product) : null;
    const effectiveCLP = sale ? sale.sale_clp : target.price_clp;
    const effectiveUSD = sale ? sale.sale_usd : target.price_usd;

    const discountedCLP = couponApplied && effectiveCLP
        ? Math.round(effectiveCLP * (100 - couponApplied.discount_pct) / 100)
        : effectiveCLP;
    const discountedUSD = couponApplied && effectiveUSD
        ? +(effectiveUSD * (100 - couponApplied.discount_pct) / 100).toFixed(2)
        : effectiveUSD;

    const requireAuth = () => {
        if (!user) {
            toast.info("Log in to purchase");
            navigate("/login", { state: { from: detailUrl } });
            return false;
        }
        return true;
    };

    const payloadFor = () => {
        const base = kind === "bundle" ? { bundle_id: bundle.id } : { product_id: product.id };
        if (couponApplied) base.coupon_code = couponApplied.code;
        return base;
    };

    const handleFlow = async () => {
        if (!requireAuth()) return;
        setLoading("flow");
        trackEvent("begin_checkout", {
            currency: "CLP",
            value: target.price_clp,
            payment_method: "flow",
            items: [{ item_id: target.id, item_name: target.name, price: target.price_clp, item_category: kind }],
        });
        try {
            const { data } = await api.post("/checkout/flow/create", payloadFor());
            if (data.redirect_url) {
                window.location.href = data.redirect_url;
            } else {
                toast.error("No payment URL received");
                setLoading("");
            }
        } catch (err) {
            toast.error(apiErrorMessage(err));
            setLoading("");
        }
    };

    const handlePayPal = async () => {
        if (!requireAuth()) return;
        setLoading("paypal");
        trackEvent("begin_checkout", {
            currency: "USD",
            value: target.price_usd,
            payment_method: "paypal",
            items: [{ item_id: target.id, item_name: target.name, price: target.price_usd, item_category: kind }],
        });
        try {
            const { data } = await api.post("/checkout/paypal/create", payloadFor());
            if (data.redirect_url) {
                window.location.href = data.redirect_url;
            } else {
                toast.error("No PayPal URL received");
                setLoading("");
            }
        } catch (err) {
            toast.error(apiErrorMessage(err));
            setLoading("");
        }
    };

    const handleTrial = async () => {
        if (!requireAuth()) return;
        setLoading("trial");
        trackEvent("begin_checkout", {
            currency: "CLP",
            value: 0,
            payment_method: "trial",
            items: [{ item_id: target.id, item_name: target.name, price: 0, item_category: "product" }],
        });
        try {
            const { data } = await api.post("/checkout/trial", { product_id: product.id });
            trackEvent("trial_started", {
                product_id: product.id,
                product_name: product.name,
                license_key: data?.license?.key,
            });
            toast.success(
                data?.already_existed
                    ? "You already had an active trial for this plugin"
                    : `${trialEligibility?.trial_days || 5}-day trial activated!`,
                { description: `License: ${data?.license?.key || ""}` }
            );
            navigate("/dashboard/licenses");
        } catch (err) {
            toast.error(apiErrorMessage(err));
            setLoading("");
        }
    };

    const [upsellOpen, setUpsellOpen] = useState(false);
    const [upsellItems, setUpsellItems] = useState([]);

    const handleFree = async () => {
        if (!requireAuth()) return;
        setLoading("free");
        trackEvent("begin_checkout", {
            currency: "CLP",
            value: 0,
            payment_method: "free",
            items: [{ item_id: target.id, item_name: target.name, price: 0, item_category: "product" }],
        });
        try {
            const { data } = await api.post("/checkout/free", { product_id: product.id });
            trackEvent("free_claim", {
                product_id: product.id,
                product_name: product.name,
                license_key: data?.license?.key,
            });
            toast.success(
                data?.already_existed
                    ? "You already had this plugin in your account"
                    : "Plugin added to your account!",
                { description: `License: ${data?.license?.key || ""}` }
            );
            // Show upsell modal with 3 paid plugins the user doesn't own yet
            try {
                const { data: items } = await api.get("/products");
                const candidates = (items || [])
                    .filter((p) => p.id !== product.id
                        && p.status !== "draft"
                        && p.status !== "coming_soon"
                        && (p.price_clp || 0) > 0)
                    .sort(() => Math.random() - 0.5)
                    .slice(0, 3);
                if (candidates.length > 0) {
                    setUpsellItems(candidates);
                    setUpsellOpen(true);
                    setLoading("");
                    return;
                }
            } catch (e) { /* ignore — fallback to direct navigation */ }
            navigate("/dashboard/licenses");
        } catch (err) {
            toast.error(apiErrorMessage(err));
            setLoading("");
        }
    };

    // A product is "free" when both prices are 0 (or null/undefined).
    const isFreeProduct = kind === "product"
        && !((product.price_clp || 0) > 0)
        && !((product.price_usd || 0) > 0);

    // Cart-related helpers
    const cartKey = kind === "bundle" ? `b:${bundle.id}` : `p:${product.id}`;
    const alreadyInCart = (cart.items || []).some((it) => it.key === cartKey);
    const [addingCart, setAddingCart] = useState(false);
    const handleAddToCart = async () => {
        setAddingCart(true);
        const res = await addItem({
            product_id: kind === "product" ? product.id : null,
            bundle_id: kind === "bundle" ? bundle.id : null,
        });
        setAddingCart(false);
        if (res.ok) {
            trackEvent("add_to_cart", {
                currency: "CLP", value: target.price_clp,
                items: [{ item_id: target.id, item_name: target.name, price: target.price_clp, item_category: kind }],
            });
        }
    };

    if (target.status === "coming_soon") {
        return (
            <div className="panel-elevated p-6" data-testid="checkout-panel">
                <div className="label-mono mb-2">/ Coming soon</div>
                <p className="text-sm text-avx-muted">This {kind === "bundle" ? "bundle" : "plugin"} is in pre-release. Subscribe to be notified when it becomes available.</p>
            </div>
        );
    }

    return (
        <div className="panel-elevated p-6" data-testid="checkout-panel">
            <div className="label-mono mb-4">/ {isFreeProduct ? "Get this plugin" : `Buy this ${kind === "bundle" ? "bundle" : "plugin"}`}</div>

            {isFreeProduct ? (
                <button
                    onClick={handleFree}
                    disabled={loading === "free"}
                    data-testid="checkout-free-btn"
                    className="w-full panel p-5 hover:border-avx-success/60 transition-colors text-left disabled:opacity-60 disabled:cursor-wait group"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="font-heading font-bold text-base flex items-center gap-2">
                                Download free
                                <span className="label-mono text-avx-success">Free · $0</span>
                            </div>
                            <div className="text-xs text-avx-muted mt-1">
                                {user
                                    ? "Permanent plugin · no card required · instant activation"
                                    : "Log in to get your free copy"}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="font-heading font-black text-avx-success text-lg">FREE</div>
                            <div className="text-[10px] font-mono text-avx-muted group-hover:text-avx-success">
                                {loading === "free" ? "Processing…" : "Claim →"}
                            </div>
                        </div>
                    </div>
                </button>
            ) : (
            <div className="space-y-3">
                {/* Add to cart */}
                {alreadyInCart ? (
                    <Link
                        to="/cart"
                        data-testid="checkout-cart-view"
                        className="w-full panel p-4 hover:border-avx-blue transition-colors text-left flex items-center justify-between gap-3 group"
                    >
                        <div>
                            <div className="font-heading font-bold text-sm text-avx-success">Already in your cart ✓</div>
                            <div className="text-xs text-avx-muted mt-1">Keep shopping or review your cart to pay for everything at once</div>
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-avx-blue group-hover:underline">
                            View cart →
                        </div>
                    </Link>
                ) : (
                    <button
                        onClick={handleAddToCart}
                        disabled={addingCart}
                        data-testid="checkout-add-to-cart"
                        className="w-full panel p-4 hover:border-avx-blue transition-colors text-left disabled:opacity-60 disabled:cursor-wait group"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <div className="font-heading font-bold text-sm flex items-center gap-2">
                                    Add to cart
                                    <span className="label-mono text-avx-blue">Buy several together</span>
                                </div>
                                <div className="text-xs text-avx-muted mt-1">
                                    Add this {kind === "bundle" ? "bundle" : "plugin"} to your cart and pay in a single checkout
                                </div>
                            </div>
                            <div className="text-right">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-avx-blue">
                                    <path d="M3 3h2l2.4 12.3a2 2 0 0 0 2 1.7h8.2a2 2 0 0 0 2-1.6L21 8H6" />
                                    <circle cx="9" cy="20" r="1.5" />
                                    <circle cx="17" cy="20" r="1.5" />
                                </svg>
                                <div className="text-[10px] font-mono text-avx-muted group-hover:text-avx-blue mt-1">
                                    {addingCart ? "Adding…" : "+ Add"}
                                </div>
                            </div>
                        </div>
                    </button>
                )}

                {/* Flow */}
                <button
                    onClick={handleFlow}
                    disabled={loading === "flow" || (providers && !providers.flow?.enabled)}
                    data-testid="checkout-flow-btn"
                    className="w-full panel p-4 hover:border-avx-blue transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="font-heading font-bold text-sm flex items-center gap-2">
                                Pay with Flow
                                <span className="label-mono text-avx-success">Chile · CLP</span>
                            </div>
                            <div className="text-xs text-avx-muted mt-1">Webpay, OnePay, Servipag, bank transfer and MercadoPago</div>
                        </div>
                        <div className="text-right">
                            {couponApplied && effectiveCLP ? (
                                <>
                                    <div className="font-mono text-[10px] text-avx-muted line-through">{formatCLP(effectiveCLP)}</div>
                                    <div className="font-heading font-bold text-avx-success">{formatCLP(discountedCLP)}</div>
                                </>
                            ) : sale ? (
                                <>
                                    <div className="font-mono text-[10px] text-avx-muted line-through">{formatCLP(sale.base_clp)}</div>
                                    <div className="font-heading font-bold text-avx-danger">{formatCLP(sale.sale_clp)}</div>
                                </>
                            ) : (
                                <div className="font-heading font-bold text-avx-blue">{formatCLP(target.price_clp)}</div>
                            )}
                            <div className="text-[10px] font-mono text-avx-muted group-hover:text-avx-blue">{loading === "flow" ? "Redirecting…" : "Continue →"}</div>
                        </div>
                    </div>
                    {providers && !providers.flow?.enabled && (
                        <div className="text-[10px] font-mono text-avx-danger mt-2">Pending configuration by the administrator</div>
                    )}
                </button>

                {/* PayPal */}
                <button
                    onClick={handlePayPal}
                    disabled={loading === "paypal" || (providers && !providers.paypal?.enabled) || !target.price_usd}
                    data-testid="checkout-paypal-btn"
                    className="w-full panel p-4 hover:border-avx-blue transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="font-heading font-bold text-sm flex items-center gap-2">
                                Pay with PayPal
                                <span className="label-mono text-avx-info">International · USD</span>
                            </div>
                            <div className="text-xs text-avx-muted mt-1">Cards worldwide + PayPal accounts</div>
                        </div>
                        <div className="text-right">
                            {couponApplied && effectiveUSD ? (
                                <>
                                    <div className="font-mono text-[10px] text-avx-muted line-through">{formatUSD(effectiveUSD)}</div>
                                    <div className="font-heading font-bold text-avx-success">{formatUSD(discountedUSD)}</div>
                                </>
                            ) : sale ? (
                                <>
                                    <div className="font-mono text-[10px] text-avx-muted line-through">{formatUSD(sale.base_usd)}</div>
                                    <div className="font-heading font-bold text-avx-danger">{formatUSD(sale.sale_usd)}</div>
                                </>
                            ) : (
                                <div className="font-heading font-bold text-avx-blue">{formatUSD(target.price_usd)}</div>
                            )}
                            <div className="text-[10px] font-mono text-avx-muted group-hover:text-avx-blue">{loading === "paypal" ? "Redirecting…" : "Continue →"}</div>
                        </div>
                    </div>
                    {providers && !providers.paypal?.enabled && (
                        <div className="text-[10px] font-mono text-avx-danger mt-2">Pending configuration by the administrator</div>
                    )}
                </button>

                {/* Trial · only for individual products + only if user can take it */}
                {kind === "product" && (
                    <TrialBlock
                        user={user}
                        eligibility={trialEligibility}
                        loading={loading === "trial"}
                        onStart={handleTrial}
                    />
                )}

                {/* Coupon input — appears for both products and bundles (paid items only) */}
                <CouponBlock
                    user={user}
                    code={couponInput}
                    setCode={setCouponInput}
                    applied={couponApplied}
                    error={couponError}
                    validating={couponValidating}
                    onApply={() => applyCoupon()}
                    onRemove={removeCoupon}
                />
            </div>
            )}

            <div className="mt-4 pt-4 border-t border-avx-line text-[10px] font-mono text-avx-muted leading-relaxed">
                {isFreeProduct
                    ? "You'll receive a permanent license. Download the plugin from the AVX Pro client or your private dashboard."
                    : "Payments processed by Flow.cl and PayPal. Plugins become available automatically in your private dashboard once payment is confirmed."}
            </div>

            <FreeUpsellModal
                open={upsellOpen}
                onClose={() => { setUpsellOpen(false); navigate("/dashboard/licenses"); }}
                onStayHere={() => { setUpsellOpen(false); }}
                items={upsellItems}
            />
        </div>
    );
}


function TrialBlock({ user, eligibility, loading, onStart }) {
    // Three states:
    //  - User logged-out → show "Inicia sesión para probar 5 días gratis"
    //  - User logged-in & eligibility not loaded → show neutral CTA
    //  - User already_owns → hide entirely (rule 4a)
    //  - User already_trialed → show disabled "Ya usaste tu trial"
    if (user && eligibility?.reason === "already_owns") return null;

    const days = eligibility?.trial_days || 5;
    const isAlreadyTrialed = eligibility?.reason === "already_trialed";
    const disabled = loading || isAlreadyTrialed;

    return (
        <div className="pt-3 mt-1 border-t border-avx-line/60" data-testid="checkout-trial-block">
            <button
                onClick={onStart}
                disabled={disabled}
                data-testid="checkout-trial-btn"
                className={`w-full panel p-4 text-left transition-colors group ${
                    isAlreadyTrialed
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:border-avx-success/60"
                }`}
            >
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="font-heading font-bold text-sm flex items-center gap-2">
                            Try {days} days free
                            <span className="label-mono text-avx-success">Trial · $0</span>
                        </div>
                        <div className="text-xs text-avx-muted mt-1">
                            {isAlreadyTrialed
                                ? "You already activated this plugin's trial"
                                : !user
                                    ? `Log in and get a ${eligibility?.trial_days || 5}-day license`
                                    : "Instant license · 1 machine · no card"}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="font-heading font-bold text-avx-success">FREE</div>
                        <div className="text-[10px] font-mono text-avx-muted group-hover:text-avx-success">
                            {loading ? "Activating…" : isAlreadyTrialed ? "Used" : "Activate →"}
                        </div>
                    </div>
                </div>
            </button>
        </div>
    );
}


function CouponBlock({ user, code, setCode, applied, error, validating, onApply, onRemove }) {
    if (applied) {
        return (
            <div className="pt-3 mt-1 border-t border-avx-line/60" data-testid="checkout-coupon-applied">
                <div className="panel p-3 bg-avx-success/8 border-avx-success/40 flex items-center justify-between gap-3">
                    <div>
                        <div className="font-heading font-bold text-sm flex items-center gap-2">
                            <span className="font-mono text-avx-success">{applied.code}</span>
                            <span className="text-avx-success">−{applied.discount_pct}%</span>
                        </div>
                        <div className="text-[10px] font-mono text-avx-muted mt-1">Coupon applied · discount shown above</div>
                    </div>
                    <button onClick={onRemove} type="button" className="text-[10px] font-mono uppercase tracking-[0.18em] text-avx-danger hover:underline" data-testid="checkout-coupon-remove">
                        Remove
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className="pt-3 mt-1 border-t border-avx-line/60" data-testid="checkout-coupon-block">
            <div className="label-mono mb-2">/ Have a coupon?</div>
            <div className="flex gap-2">
                <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]+/g, ""))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onApply(); } }}
                    placeholder="BLACKFRIDAY"
                    className="flex-1 bg-avx-bg border border-avx-line h-10 px-3 rounded-sm font-mono text-xs uppercase focus:outline-none focus:border-avx-blue"
                    data-testid="checkout-coupon-input"
                    disabled={!user || validating}
                />
                <button
                    type="button"
                    onClick={onApply}
                    disabled={!user || !code || validating}
                    className="btn-secondary px-4 py-2 text-[10px] font-mono uppercase tracking-[0.18em] rounded-sm disabled:opacity-50"
                    data-testid="checkout-coupon-apply-btn"
                >
                    {validating ? "..." : "Apply"}
                </button>
            </div>
            {!user && (
                <div className="text-[10px] font-mono text-avx-muted mt-2">Log in to use a coupon.</div>
            )}
            {error && (
                <div className="text-[10px] font-mono text-avx-danger mt-2" data-testid="checkout-coupon-error">{error}</div>
            )}
        </div>
    );
}



function FreeUpsellModal({ open, onClose, onStayHere, items }) {
    const resolveImg = (url) => {
        if (!url) return "";
        if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
        return url;
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="bg-avx-surface border-avx-line max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Done! We also recommend…</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-avx-muted -mt-2 mb-4">
                    Other AVX Pro plugins that pair perfectly with your new free download.
                </p>
                <div className="grid sm:grid-cols-3 gap-4" data-testid="upsell-grid">
                    {items.map((p) => (
                        <Link
                            key={p.id}
                            to={`/plugins/${p.slug}`}
                            onClick={onClose}
                            className="panel-elevated overflow-hidden hover:border-avx-blue/60 transition-colors group"
                            data-testid={`upsell-item-${p.slug}`}
                        >
                            <div className="aspect-square bg-avx-bg/60 border-b border-avx-line overflow-hidden">
                                {p.image_url ? (
                                    <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <span className="font-heading font-black text-5xl text-avx-line">{p.name.charAt(0)}</span>
                                    </div>
                                )}
                            </div>
                            <div className="p-3">
                                <div className="font-heading font-bold text-sm truncate">{p.name}</div>
                                <div className="font-mono text-avx-blue text-xs mt-1">{formatCLP(p.price_clp)} · {formatUSD(p.price_usd)}</div>
                            </div>
                        </Link>
                    ))}
                </div>
                <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-avx-line">
                    <button onClick={onStayHere} className="text-xs font-mono uppercase tracking-[0.2em] text-avx-muted hover:text-avx-text px-3 py-2" data-testid="upsell-dismiss">
                        Keep exploring
                    </button>
                    <button onClick={onClose} className="btn-primary px-5 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="upsell-go-licenses">
                        Go to my licenses →
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
